import React, { useState, useEffect, useRef } from "react";
import { Modal, Spin } from "antd";
import { pdf, Document, Page, Image, PDFViewer, StyleSheet } from "@react-pdf/renderer";
import html2canvas from "html2canvas";

// Define styles for PDF
const styles = StyleSheet.create({
  page: {
    paddingVertical: 10, // 10px top margin
    paddingHorizontal: 10, // 10px side margins
    flexDirection: 'column',
  },
  image: {
    width: '100%',
  },
});

interface PDFViewerModalProps {
  open: boolean;
  onClose: () => void;
  gridRef: React.RefObject<HTMLDivElement>;
}

interface GridItemPosition {
  top: number;
  bottom: number;
  height: number;
}

interface PDFPage {
  image: string;
  height: number;
  width: number;
}

const PDFViewerModal: React.FC<PDFViewerModalProps> = ({ open, onClose, gridRef }) => {
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pages, setPages] = useState<PDFPage[]>([]);

  const generatePDF = async () => {
    if (!gridRef.current) return;

    setIsGenerating(true);
    setPages([]);
    
    try {
      // Capture the entire grid with exact dimensions
      const canvas = await html2canvas(gridRef.current, {
        scale: 1,
        useCORS: true,
        logging: false,
        backgroundColor: "#FFFFFF",
        windowHeight: gridRef.current.scrollHeight,
      });

      // Get all grid items' positions
      const items = Array.from(gridRef.current.querySelectorAll('.react-grid-item'));
      const itemPositions: GridItemPosition[] = items.map(item => {
        const rect = item.getBoundingClientRect();
        const containerRect = gridRef.current!.getBoundingClientRect();
        return {
          top: rect.top - containerRect.top,
          bottom: rect.bottom - containerRect.top,
          height: rect.height
        };
      });

      // Split into pages with proper margins and alignment
      const generatedPages = splitIntoPages(canvas, itemPositions);
      setPages(generatedPages);

      // Create PDF with proper margins
      const blob = await pdf(
        <Document>
          {generatedPages.map((page, index) => (
            <Page 
              key={index} 
              size="A4" 
              style={styles.page}
            >
              <Image 
                src={page.image} 
                style={[styles.image, { 
                  height: page.height,
                }]} 
              />
            </Page>
          ))}
        </Document>
      ).toBlob();

      setPdfBlob(blob);
    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const splitIntoPages = (canvas: HTMLCanvasElement, itemPositions: GridItemPosition[]): PDFPage[] => {
    const PX_TO_PT = 0.5;
    const PAGE_WIDTH_PT = 595.28; // A4 width in points
    const PAGE_HEIGHT_PT = 842; // A4 height in points
    const MARGIN_PT = 10 * PX_TO_PT; // 10px margin in points
    
    const availableHeight = PAGE_HEIGHT_PT - MARGIN_PT; // Account for top margin
    const pages: PDFPage[] = [];
    let currentY = 0;

    while (currentY < canvas.height) {
      // Find the optimal break point
      let breakY = currentY + (availableHeight / PX_TO_PT);
      
      // Ensure we don't cut through any items
      for (const item of itemPositions) {
        if (item.top < breakY && item.bottom > breakY) {
          breakY = item.bottom;
        }
      }

      breakY = Math.min(breakY, canvas.height);
      
      const pageHeightPt = (breakY - currentY) * PX_TO_PT;
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = breakY - currentY;
      
      const ctx = pageCanvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(
          canvas,
          0, currentY, canvas.width, pageCanvas.height,
          0, 0, canvas.width, pageCanvas.height
        );
      }
      
      pages.push({
        image: pageCanvas.toDataURL("image/jpeg", 0.9),
        height: pageHeightPt,
        width: PAGE_WIDTH_PT - (MARGIN_PT * 2) // Account for side margins
      });
      
      currentY = breakY;
    }

    return pages;
  };

  useEffect(() => {
    if (open) {
      generatePDF();
    } else {
      setPdfBlob(null);
      setPages([]);
    }
  }, [open]);

  return (
    <Modal
      title="PDF Preview"
      width={800}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {isGenerating ? (
        <div className="flex justify-center items-center h-[500px]">
          <Spin size="large" tip="Generating PDF..." />
        </div>
      ) : pages.length > 0 ? (
        <PDFViewer style={{ width: "100%", height: "500px" }}>
          <Document>
            {pages.map((page, index) => (
              <Page key={index} size="A4" style={styles.page}>
                <Image 
                  src={page.image} 
                  style={[styles.image, { 
                    height: page.height,
                  }]} 
                />
              </Page>
            ))}
          </Document>
        </PDFViewer>
      ) : (
        <div className="flex justify-center items-center h-[500px]">
          <p>Failed to generate PDF preview</p>
        </div>
      )}
    </Modal>
  );
};

export default PDFViewerModal;